import { type ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';

/** Idiomas suportados no app do viajante (pt-BR e es). */
export type Lang = 'pt' | 'es';
export const LANGS: Lang[] = ['pt', 'es'];
export const LANG_LABELS: Record<Lang, string> = { pt: 'Português', es: 'Español' };

const STORAGE_KEY = 'serviquim.lang';
const DEFAULT_LANG: Lang = 'pt';

type Messages = Record<string, unknown>;

/**
 * Dicionários por namespace, auto-carregados de `messages/*.ts` (cada arquivo exporta
 * `pt` e `es`). O nome do arquivo é o namespace → chave = `namespace.caminho.da.label`.
 */
const modules = import.meta.glob<{ pt?: Messages; es?: Messages }>('./messages/*.ts', {
  eager: true,
});

const DICT: Record<Lang, Messages> = { pt: {}, es: {} };
for (const [path, mod] of Object.entries(modules)) {
  const ns = path.slice(path.lastIndexOf('/') + 1, -3);
  DICT.pt[ns] = mod.pt ?? {};
  DICT.es[ns] = mod.es ?? {};
}

function lookup(dict: Messages, key: string): string | undefined {
  const value = key.split('.').reduce<unknown>((acc, part) => {
    if (acc && typeof acc === 'object') return (acc as Record<string, unknown>)[part];
    return undefined;
  }, dict);
  return typeof value === 'string' ? value : undefined;
}

function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_, name) =>
    name in params ? String(params[name]) : `{${name}}`,
  );
}

export type TFunction = (key: string, params?: Record<string, string | number>) => string;

interface I18nContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: TFunction;
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

function readInitialLang(): Lang {
  const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
  return stored === 'pt' || stored === 'es' ? stored : DEFAULT_LANG;
}

export function I18nProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [lang, setLangState] = useState<Lang>(readInitialLang);

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* localStorage indisponível — segue só em memória */
    }
  }, []);

  const t = useCallback<TFunction>(
    (key, params) => {
      const msg = lookup(DICT[lang], key) ?? lookup(DICT.pt, key) ?? key;
      return interpolate(msg, params);
    },
    [lang],
  );

  const value = useMemo<I18nContextValue>(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** Hook de tradução: `const { t, lang, setLang } = useT()` → `t('trips.title')`. */
export function useT(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error('useT deve ser usado dentro de <I18nProvider>');
  }
  return ctx;
}
